/**
 * GPS Provider Settings Page
 *
 * Configure per-tenant GPS provider credentials (Global Track / Sarekon,
 * PassTime, etc.). Mirrors the AI Provider settings pattern: secrets are sent
 * to the backend, encrypted at rest, and never returned in plaintext — the API
 * only reports whether a provider is configured (+ a last-4 hint).
 *
 * Backend contract:
 *   GET  /settings/integrations/gps -> { provider, configured, last4, available_providers }
 *   POST /settings/integrations/gps  { provider, username?, password?, access_key? }
 */
import {
  CheckCircleIcon,
  InformationCircleIcon,
  MapPinIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';

import PasswordInput from '../../components/ui/PasswordInput';
import { useAuth } from '../../contexts/AuthContext';
import apiService, { extractApiErrorMessage } from '../../services/api';
import { canManageUsers } from '../../utils/roles';

interface GPSProviderOption {
  key: string;
  label: string;
  fields: string[]; // subset of: 'username', 'password', 'access_key'
  get_key_url: string | null;
}

interface GPSIntegrationSettings {
  provider: string | null;
  configured: boolean;
  last4: string | null;
  available_providers: GPSProviderOption[];
}

// Tenant-level dealer liability acknowledgment for remote starter-interrupt
// (WS1c-2 gate condition). Read here so the UI never shows a stale state.
interface AckStatus {
  liability_acknowledged: boolean;
  accepted_by_name?: string | null;
  accepted_by_email?: string | null;
  accepted_at?: string | null;
}

// "by Name (email)" when both known, gracefully degrading to whichever is present.
const formatAcceptedBy = (ack: AckStatus): string => {
  const { accepted_by_name: name, accepted_by_email: email } = ack;
  if (name && email) return ` by ${name} (${email})`;
  if (name) return ` by ${name}`;
  if (email) return ` by ${email}`;
  return '';
};

// Used only if the backend doesn't return available_providers (defensive).
const FALLBACK_PROVIDERS: GPSProviderOption[] = [
  {
    key: 'globaltrack',
    label: 'Global Track (Sarekon)',
    fields: ['username', 'password'],
    get_key_url: 'https://globaltrack.sarekon.com',
  },
  { key: 'passtime', label: 'PassTime', fields: ['access_key'], get_key_url: null },
  { key: 'payment_watch', label: 'PaymentWatch', fields: ['access_key'], get_key_url: null },
  { key: 'imetrik', label: 'iMetrik', fields: ['access_key'], get_key_url: null },
];

const FIELD_LABELS: Record<string, string> = {
  username: 'Username',
  password: 'Password',
  access_key: 'Access Key / API Token',
};

const GPSProviderSettingsPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<GPSIntegrationSettings | null>(null);
  const [selectedProvider, setSelectedProvider] = useState<string>('');
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [ack, setAck] = useState<AckStatus | null>(null);
  const [ackSaving, setAckSaving] = useState(false);
  const [showWhy, setShowWhy] = useState(false);

  const { user } = useAuth();
  // The dealer liability ack is the dealership's legal acceptance — mirror the
  // backend require_admin (system_admin only) write gate. Managers see it read-only.
  const canAcknowledge = canManageUsers(user?.role);

  const fetchAck = async () => {
    try {
      const data = await apiService.get<AckStatus>('/gps/starter-interrupt/acknowledgment');
      setAck(data);
    } catch {
      setAck({ liability_acknowledged: false });
    }
  };

  const handleAckChange = async (value: boolean) => {
    try {
      setAckSaving(true);
      await apiService.post('/gps/starter-interrupt/acknowledgment', { acknowledged: value });
      toast.success(
        value ? 'Liability acknowledgment recorded.' : 'Liability acknowledgment revoked.'
      );
      fetchAck();
    } catch (error) {
      toast.error(extractApiErrorMessage(error, 'Failed to update acknowledgment'));
    } finally {
      setAckSaving(false);
    }
  };

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const data = await apiService.get<GPSIntegrationSettings>('/settings/integrations/gps');
      const providers =
        data.available_providers && data.available_providers.length > 0
          ? data.available_providers
          : FALLBACK_PROVIDERS;
      setSettings({ ...data, available_providers: providers });
      setSelectedProvider(data.provider || providers[0]?.key || '');
    } catch (error) {
      // Keep the select usable even if the load failed (avoids an empty value).
      setSelectedProvider((prev) => prev || FALLBACK_PROVIDERS[0].key);
      toast.error(extractApiErrorMessage(error, 'Failed to load GPS provider settings'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
    fetchAck();
  }, []);

  const providers = settings?.available_providers ?? FALLBACK_PROVIDERS;
  const currentProvider = providers.find((p) => p.key === selectedProvider) ?? providers[0];

  const handleSave = async () => {
    if (!currentProvider) return;
    const payload: Record<string, string> = { provider: currentProvider.key };
    for (const field of currentProvider.fields) {
      const value = fieldValues[field]?.trim();
      if (value) payload[field] = value;
    }
    if (Object.keys(payload).length === 1) {
      toast.error('Enter the provider credentials before saving.');
      return;
    }

    try {
      setSaving(true);
      const data = await apiService.post<GPSIntegrationSettings>(
        '/settings/integrations/gps',
        payload
      );
      const provs =
        data.available_providers && data.available_providers.length > 0
          ? data.available_providers
          : providers;
      setSettings({ ...data, available_providers: provs });
      setFieldValues({});
      toast.success('GPS provider credentials saved.');
    } catch (error) {
      toast.error(extractApiErrorMessage(error, 'Failed to save GPS provider credentials'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 w-48 rounded bg-gray-200" />
          <div className="h-32 rounded bg-gray-100" />
        </div>
      </div>
    );
  }

  const isConfigured = settings?.configured ?? false;

  return (
    <div className="p-6">
      <div className="mb-6 flex items-start gap-3">
        <MapPinIcon className="mt-1 h-6 w-6 text-gray-400" />
        <div>
          <h2 className="text-xl font-semibold text-gray-900">GPS Tracking</h2>
          <p className="text-sm text-gray-600">
            Connect your GPS provider so remote disable/enable and location tracking can reach your
            devices. Credentials are encrypted and never shown again.
          </p>
        </div>
      </div>

      {/* Status */}
      <div className="mb-6 flex items-center gap-2 rounded-lg border bg-gray-50 p-4">
        {isConfigured ? (
          <>
            <CheckCircleIcon className="h-5 w-5 text-green-600" />
            <span className="text-sm font-medium text-green-700">
              {(providers.find((p) => p.key === settings?.provider)?.label ?? settings?.provider) ||
                'Provider'}{' '}
              configured
              {settings?.last4 ? ` (…${settings.last4})` : ''}
            </span>
          </>
        ) : (
          <>
            <XCircleIcon className="h-5 w-5 text-amber-500" />
            <span className="text-sm font-medium text-amber-700">
              No GPS provider configured — remote disable is unavailable until you add credentials.
            </span>
          </>
        )}
      </div>

      <div className="max-w-xl space-y-5 rounded-lg border bg-white p-5">
        {/* Provider */}
        <div>
          <label className="mb-1 block text-sm font-semibold text-gray-700">GPS Provider</label>
          <select
            value={selectedProvider}
            onChange={(e) => {
              setSelectedProvider(e.target.value);
              setFieldValues({});
            }}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            {providers.map((p) => (
              <option key={p.key} value={p.key}>
                {p.label}
              </option>
            ))}
          </select>
        </div>

        {/* Credential fields */}
        {currentProvider?.fields.map((field) => (
          <div key={field}>
            <label className="mb-1 block text-sm font-semibold text-gray-700">
              {FIELD_LABELS[field] || field}
              {isConfigured && settings?.provider === currentProvider.key && (
                <span className="ml-1 font-normal text-green-600">(configured)</span>
              )}
            </label>
            {field === 'username' ? (
              <input
                type="text"
                autoComplete="off"
                value={fieldValues[field] || ''}
                onChange={(e) => setFieldValues((prev) => ({ ...prev, [field]: e.target.value }))}
                placeholder={
                  isConfigured && settings?.provider === currentProvider.key
                    ? 'Enter new value to replace existing'
                    : `Enter ${FIELD_LABELS[field] || field}`
                }
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            ) : (
              <PasswordInput
                autoComplete="off"
                value={fieldValues[field] || ''}
                onChange={(e) => setFieldValues((prev) => ({ ...prev, [field]: e.target.value }))}
                placeholder={
                  isConfigured && settings?.provider === currentProvider.key
                    ? 'Enter new value to replace existing'
                    : `Enter ${FIELD_LABELS[field] || field}`
                }
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            )}
          </div>
        ))}

        {currentProvider?.get_key_url && (
          <p className="text-sm text-gray-500">
            Don&apos;t have credentials?{' '}
            <a
              href={currentProvider.get_key_url}
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-blue-600 underline hover:text-blue-800"
            >
              Get them from {currentProvider.label}
            </a>
          </p>
        )}

        <div className="flex justify-end">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save Credentials'}
          </button>
        </div>
      </div>

      {/* Dealer liability acknowledgment — the dealership's legal acceptance of
          responsibility for remote starter-interrupt. A required, fail-closed
          condition of the WS1c-2 gate: without it on file, every remote disable
          is blocked. Recording it is admin-only (mirrors backend require_admin);
          managers see the state read-only. */}
      <div className="mt-6 max-w-xl rounded-lg border bg-white p-5">
        <div className="flex items-center gap-1.5">
          <h3 className="text-sm font-semibold text-gray-900">Dealer Liability Acknowledgment</h3>
          <button
            type="button"
            onClick={() => setShowWhy(true)}
            aria-label="Why is this required?"
            className="text-gray-400 hover:text-blue-600"
          >
            <InformationCircleIcon className="h-4 w-4" />
          </button>
        </div>
        <p className="mt-1 text-sm text-gray-600">
          Remote starter-interrupt (vehicle disable) is blocked until the dealership accepts
          liability for its use. Required in addition to per-vehicle customer consent.
        </p>

        <div className="mt-4 flex items-center gap-2">
          {ack?.liability_acknowledged ? (
            <>
              <CheckCircleIcon className="h-5 w-5 text-green-600" />
              <span className="text-sm font-medium text-green-700">
                Liability acknowledged{formatAcceptedBy(ack)}
              </span>
            </>
          ) : (
            <>
              <XCircleIcon className="h-5 w-5 text-amber-500" />
              <span className="text-sm font-medium text-amber-700">
                Not acknowledged — remote disable is unavailable until accepted.
              </span>
            </>
          )}
        </div>

        {canAcknowledge ? (
          <div className="mt-4 flex justify-end">
            <button
              type="button"
              onClick={() => handleAckChange(!ack?.liability_acknowledged)}
              disabled={ackSaving}
              className={`rounded-md px-4 py-2 text-sm font-semibold text-white disabled:opacity-50 ${
                ack?.liability_acknowledged
                  ? 'bg-gray-600 hover:bg-gray-700'
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {ack?.liability_acknowledged ? 'Revoke acknowledgment' : 'Acknowledge liability'}
            </button>
          </div>
        ) : (
          <p className="mt-3 text-xs text-gray-500">
            Only a system administrator can accept or revoke this acknowledgment.
          </p>
        )}
      </div>

      {/* Plain-language "why" dialog for non-technical dealership staff. */}
      {showWhy && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-gray-600 bg-opacity-50 p-4"
          onClick={() => setShowWhy(false)}
        >
          <div
            className="max-w-lg rounded-lg bg-white p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-gray-900">Why acknowledge liability?</h3>
            <div className="mt-3 space-y-3 text-sm text-gray-700">
              <p>
                Remotely disabling a customer&apos;s vehicle is a serious action with legal
                consequences. If a vehicle is disabled at an unsafe time or place,{' '}
                <span className="font-medium">your dealership is responsible</span> — not
                SimpleDealer.
              </p>
              <p>By accepting, an administrator confirms that your dealership will:</p>
              <ul className="list-disc space-y-1 pl-5">
                <li>
                  Only use remote disable lawfully — for genuine payment default, never as
                  harassment or a scare tactic.
                </li>
                <li>
                  Follow your state&apos;s lending and repossession laws when disabling a vehicle.
                </li>
                <li>Never disable a vehicle in a way that could endanger the driver.</li>
              </ul>
              <p>
                This is a one-time, dealership-wide acceptance. It is separate from the consent each
                customer signs for their own vehicle. Until it is on file, SimpleDealer keeps remote
                disable switched off — for your protection and theirs.
              </p>
            </div>
            <div className="mt-5 flex justify-end">
              <button
                type="button"
                onClick={() => setShowWhy(false)}
                className="rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GPSProviderSettingsPage;
